import {
  Button,
  DialogActions,
  DialogContent,
  makeStyles
} from '@material-ui/core';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { DimensionObjectType } from '~/types';
import { Hours, LoadingButton } from '~/modules/common/components';
import { RoleDropdown, ResourceRoleDropdown } from '~/modules/tasks/components';
import { useIsRateTableDimensionEnabled } from '~/modules/rateCard/advancedRateCard/components/hooks';
import { ESTIMATED_HOURS_MAX } from './constants';
import useResourceEstimateSubmit from './useResourceEstimateSubmit';

const useStyles = makeStyles(theme => ({
  actions: {
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(1)
  },
  spacing: {
    marginBottom: theme.spacing(2)
  },
  numberInputWithValue: {
    marginBottom: theme.spacing(2),
    left: 0,
    right: 'unset',
    transformOrigin: 'left top'
  },
  numberInputWithoutValue: {
    marginBottom: theme.spacing(2),
    right: theme.spacing(3),
    left: 0,
    transformOrigin: 'left top',
    '&.Mui-focused': {
      right: 'unset',
      left: 0,
      transformOrigin: 'left top'
    }
  }
}));

const PlaceholderForm = ({ task, onClose, isEditMode, setIsDirty }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const {
    values: { role, initialEstimatedHours },
    setFieldValue,
    dirty,
    isSubmitting,
    submitForm
  } = useFormikContext();

  const {
    projectReference: { id: projectId }
  } = task;

  const {
    loading: rateTableDimensionLoading,
    isDimensionEnabled: isRoleRateTableDimensionEnabled
  } = useIsRateTableDimensionEnabled({
    projectId,
    dimensionType: DimensionObjectType.ProjectRole
  });

  const onRoleChange = useCallback(value => setFieldValue('role', value), [
    setFieldValue
  ]);

  const onAddRoleDialogCancel = useCallback(() => setFieldValue('role', null), [
    setFieldValue
  ]);

  const onInitialEstimatedHoursChange = useCallback(
    ({ target: { value: estimatedHours } }) =>
      setFieldValue(
        'initialEstimatedHours',
        estimatedHours && Math.min(estimatedHours, ESTIMATED_HOURS_MAX)
      ),
    [setFieldValue]
  );

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty, setIsDirty]);

  const isDisabled = !role || (!dirty && isEditMode) || isSubmitting;

  return (
    <>
      <DialogContent>
        {rateTableDimensionLoading || isRoleRateTableDimensionEnabled ? (
          <ResourceRoleDropdown
            value={role}
            onChange={onRoleChange}
            variant="outlined"
            placeholder={formatMessage({
              id: 'addResourceEstimateDialog.role'
            })}
            className={classes.spacing}
            label={formatMessage({ id: 'addResourceEstimateDialog.role' })}
            onAddRoleDialogCancelButtonClick={onAddRoleDialogCancel}
            autoFocus
            projectId={projectId}
            rateTableDimensionLoading={rateTableDimensionLoading}
          />
        ) : (
          <RoleDropdown
            value={role}
            onChange={onRoleChange}
            variant="outlined"
            placeholder={formatMessage({
              id: 'addResourceEstimateDialog.role'
            })}
            className={classes.spacing}
            label={formatMessage({ id: 'addResourceEstimateDialog.role' })}
            onAddRoleDialogCancelButtonClick={onAddRoleDialogCancel}
            autoFocus
          />
        )}
        <Hours
          className={
            initialEstimatedHours
              ? classes.numberInputWithValue
              : classes.numberInputWithoutValue
          }
          variant="outlined"
          data-qe-id="AddResourceEstimateDialogEstimatedHoursField"
          id="AddResourceEstimateDialogEstimatedHoursField"
          label={formatMessage({
            id: 'addResourceEstimateDialog.estimatedHours'
          })}
          ariaLabel={formatMessage({
            id: 'addResourceEstimateDialog.estimatedHours'
          })}
          margin="none"
          isEditible
          align="left"
          onChange={onInitialEstimatedHoursChange}
          value={initialEstimatedHours}
          precision={2}
        />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant="contained">
          {formatMessage({ id: 'button.cancel' })}
        </Button>
        <LoadingButton
          color="primary"
          onClick={submitForm}
          variant="contained"
          disabled={isDisabled}
          isLoading={isSubmitting}
        >
          {formatMessage({
            id: isEditMode
              ? 'editResourceEstimateDialog.savePlaceholder'
              : 'addResourceEstimateDialog.addPlaceholder'
          })}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

PlaceholderForm.propTypes = {
  task: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func.isRequired
};

const PlaceholderFormContent = ({
  task,
  onClose,
  isEditMode,
  setIsDirty,
  computedInitialHours,
  resourceEstimate,
  resourceEstimates,
  setParentFieldValue,
  canViewCost
}) => {
  const { onResourceEstimateSubmit } = useResourceEstimateSubmit({
    task,
    canViewCost,
    resourceEstimates,
    setParentFieldValue,
    onClose
  });

  const initialValues = useMemo(
    () => ({
      role: isEditMode
        ? resourceEstimate.projectRole
        : task.assignedRole || null,
      initialEstimatedHours: computedInitialHours,
      taskResourceEstimateId: isEditMode
        ? resourceEstimate.taskResourceEstimateId
        : null
    }),
    [isEditMode, resourceEstimate, task, computedInitialHours]
  );

  const handleSubmit = useCallback(
    async (
      { role, initialEstimatedHours, taskResourceEstimateId },
      { setSubmitting }
    ) => {
      await onResourceEstimateSubmit({
        input: {
          projectRoleId: role?.id,
          initialEstimatedHours,
          taskResourceEstimateId
        },
        role,
        setSubmitting
      });
    },
    [onResourceEstimateSubmit]
  );

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      <PlaceholderForm
        task={task}
        onClose={onClose}
        isEditMode={isEditMode}
        setIsDirty={setIsDirty}
      />
    </Formik>
  );
};

PlaceholderFormContent.propTypes = {
  task: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  setIsDirty: PropTypes.func.isRequired,
  computedInitialHours: PropTypes.number,
  resourceEstimate: PropTypes.object,
  resourceEstimates: PropTypes.array.isRequired,
  setParentFieldValue: PropTypes.func.isRequired,
  canViewCost: PropTypes.bool.isRequired
};

export default PlaceholderFormContent;
